<template lang="pug">
	.easy-creative--audio-generator
		.flex-col.space-large
			.line-holder(ref="lineHolder")
				.line-loader(v-if="currentStep", :style="{ '--percentage': `${statePercent}%` }", :text="stateText",
					:class="{ success: finishedState && !isDownloadingFiles }")
				canvas.waveform-canvas(ref="canvas", :width="canvasWidth", height="30px", :style="{ height: '30px' }")
			.flex-col.space-large
				.flex-row.space-medium.align-center.center.wrap(v-if="isFinished")
					.play-button.flex-row.space.align-center.center(@click="togglePlay")
						fas(:icon="isPlaying ? 'pause' : 'play'")
						span {{isPlaying ? $tCaption('Stop', '@actions') : $tCaption('Play', '@actions')}}
					.play-button.flex-row.space.align-center.center(@click="download", :class="{ 'disabled-controls': isDownloading || !finishedState }")
						fas(:icon="isDownloading ? 'spinner' : 'download'", :spin="isDownloading")
						span {{$tCaption('Download')}}
					.play-button.flex-row.space.align-center.center(v-if="info && info.brief && info.brief.ShowProducerRequest", @click="info.producerRequest().show()", :class="{ 'disabled-controls': !!isLoading || !finishedState || (hasNotLoadedVoices && !defaultVoicesProcessed) }")
						fas(icon="message")
						span {{$tCaption('Ask expert')}}
				DxGallery(v-if="images && images.length", :data-source="images", :loop="true", :show-nav-buttons="true", :show-indicator="true" :height="330")
					template(v-slot:item="{ data }")
						.flex-col.space
							.font-big.upper.grey {{data.promptHint || data.promptName}}
							.image-item.flex-row.center
								img(:src="data.imageUrl")
				Loader(v-if="generatingImages", :text="$tCaption('Generating images')")
				.spacer
				.flex-col.space-medium.full-width
					.actions-divider.full-width
					.flex-row.max-limited.navigation-row.justify
						.flex-row.space
							.fast-change-button.flex-row.space.align-center.clickable.hoverable(v-if="info.fastChange.enabled", @click="info.fastChange.visible = !info.fastChange.visible", 
								:class="{ active: info.fastChange.visible  }")
								.icon
									fas(:icon="info.fastChange.visible ? 'fas fa-square-check' : 'far fa-square'")
								span {{$tCaption('Fast change')}}
						.flex-row.space
							DxButton(:text="$tCaption('Back', '@actions')", @click="back", :activeStateEnabled="false", :focusStateEnabled="false")
							DxButton(:text="$tCaption('Next', '@actions')", type="default", @click="next", :activeStateEnabled="false", :focusStateEnabled="false", :disabled="!!isLoading || !finishedState || (hasNotLoadedVoices && !defaultVoicesProcessed)")
					slot(name="fast-change")
		.hidden
			CreativeDetail(ref="creativeDetail", v-if="creativeOpts", :opts="creativeOpts", :forceTracker="true", @loaded="onCreativeLoaded", @play-state="v => isPlaying = v", 
				@default-voices-replacing="onDefaultVoicesReplacing", @default-voices-replaced="onDefaultVoicesReplaced")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import DxButton from 'devextreme-vue/button';
	import DxTextArea from 'devextreme-vue/text-area';
	import CreativeDetail from 'view/creative/Detail.vue';
	import helper from './helper';
	import DxGallery from 'devextreme-vue/gallery';
	import creativeUpdater from 'view/creative/updater';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative-audio-generator',
		props: ['info'],
		components: {
			DxButton, Loader, DxTextArea, CreativeDetail, DxGallery
		},
		data() {
			const activatingVoicesStep = {
				text: this.$tCaption('Activating voices'),
				time: 60000,
				isPersonalTime: true,
				getTime: () => this.info?.scriptBuilder()?.voicesActivatedTime ? (this.info.scriptBuilder().leftForVoicesActivation * 1000 || 0) : 60000
			};
			return {
				isLoading: false,
				creativeOpts: null,
				creativeTotalTime: null,
				waitingSteps: [
					{
						text: this.$tCaption('Initialization'),
						time: 1000
					},
					{
						text: this.$tCaption('Script processing'),
						time: 2500
					}, 
					{
						text: this.$tCaption('Initiating new creative'),
						time: 1500
					},
					{
						text: this.$tCaption('Preparing voices'),
						time: 20000,
						isPersonalTime: true
					},
					{
						text: this.info.musicSelection()?.selectedMedia ? this.$tCaption('Processing music') : this.$tCaption('Searching music'),
						time: 20000,
						isPersonalTime: true
					},
					{
						text: this.$tCaption('Searching sound effects'),
						time: 20000,
						isPersonalTime: true
					},
					{
						text: this.$tCaption('Building audio'),
						time: 20000,
						isPersonalTime: true
					},
					activatingVoicesStep
				],
				activatingVoicesStep,
				currentStepIdx: null,
				currentPercentage: 0,
				waitedTime: 0,
				isFinished: false,
				isPlaying: false,
				isDownloading: false,
				filesDownloadingState: {},
				hasNotLoadedVoices: false,
				defaultVoicesProcessed: false,
				canvasWidth: 0,
				creative: null
			};
		},
		computed: {
			currentStep() {
				return this.waitingSteps[this.currentStepIdx];
			},
			totalTime() {
				return this.waitingSteps.filter(f => !f.isPersonalTime).reduce((total, v) => total + v.time, 0);
			},
			tracker: {
				get: function() { return this.$refs.creativeDetail?.$refs.tracker },
				cache: false
			},
			script: {
				get: function() { return this.info.scriptBuilder().script },
				cache: false
			},
			filesDownloadingPercent() {
				const keys = Object.keys(this.filesDownloadingState).filter(f => this.filesDownloadingState[f] !== 100);
				return parseInt(keys.reduce((total, key) => total + this.filesDownloadingState[key], 0) / keys.length);
			},
			isDownloadingFiles() {
				return this.filesDownloadingPercent > 0 && this.filesDownloadingPercent < 100;
			},
			stateText() {
				if (this.isDownloadingFiles) {
					const length = Object.keys(this.filesDownloadingState).length;
					return this.$tCaption('Preparing audio.. <LOADED>/<FILESCOUNT> (<PERCENTAGE>%)')
						.replace(/<PERCENTAGE>/g, this.filesDownloadingPercent)
						.replace(/<FILESCOUNT>/g, length)
						.replace(/<LOADED>/g, Math.min(length, Object.keys(this.filesDownloadingState).filter(f => this.filesDownloadingState[f] === 100).length + 1));
				}
				if (this.scriptStatus) {
					return this.scriptStatus;
				}
				return this.finishedState ? this.$tCaption('Audio generated successfully, length <SECONDSLENGTH> seconds').replace(/<SECONDSLENGTH>/g, this.creativeTotalTime || 0) : `${this.currentStep.text}..`;
			},
			statePercent() {
				if (this.isDownloadingFiles) return this.filesDownloadingPercent;

				if (this.scriptStatus) return this.scriptPercent;

				return this.finishedState ? 100 : this.currentPercentage;
			},
			finishedState() {
				return !this.scriptStatus && this.isFinished && (!this.hasNotLoadedVoices || this.defaultVoicesProcessed);
			},
			images() {
				return this.info.scriptBuilder().images;
			},
			generatingImages() {
				return this.info.scriptBuilder().generatingImages;
			},
			scriptBuilder() {
				return this.info.scriptBuilder();
			},
			scriptStatus() {
				return this.scriptBuilder?.isLoading ? this.scriptBuilder?.status : null;
			},
			scriptPercent() {
				return this.scriptBuilder?.isLoading ? this.scriptBuilder?.loadingPercent : 0;
			}
		},
		watch: {
			finishedState(v) {
				if (v) {
					this.getCreative(true);
				}
				this.info.states.audioGenerated = v;
			}
		},
		mounted() {
			if (__DEBUG__) {
				window.easyCreativeAudioGenerator = this;
				window.easyCreativeAudioGeneratorHelper = helper;
			}
			this.generate();
		},
		destroyed() {
			if (this.$nextStepTimeout) {
				clearTimeout(this.$nextStepTimeout);
				delete this.$nextStepTimeout;
			}
			delete this.$saveCreativePromise;
		},
		methods: {
			getCreative(regenerate) {
				if (!this.creative || regenerate) {
					if (!this.$saveCreativePromise || regenerate) {
						this.$saveCreativePromise = this.saveCreative().then(() => delete this.$saveCreativePromise);
					}
					return this.$saveCreativePromise;
				}

				return this.creative;
			},
			async saveCreative() {
				if (__DEBUG__)
					console.debug('Save creative');
				const creativeOpts = this.info.audioGenerator().creativeOpts;
				const questions = this.info.questions();
				const data = {
					Oid: null,
					Name: questions.getCreativeName() || (await this.info.generateCreativeName()) || this.info.name(),
					Isci: questions.getCreativeISCI(),
					JsonConfig: {
						...(creativeOpts?.creative?.JsonConfig || {}),
						creativeVersion: creativeUpdater.CREATIVE_VERSION,
						debugInfo: this.info.getDebugInfo()
					},
					IsTemporary: true,
					Duration: parseInt(_.max((creativeOpts?.insertions || []).map(f => (f.StartTime + f.Duration - (f.StartOffset || 0) - (f.EndOffset || 0)) || 0) || 0) || 0)
				};

				const scriptBuilder = this.info.scriptBuilder();
				if (questions) {
					data.JsonConfig.brief = {
						briefId: questions.brief?.Oid,
						results: questions.getFormedResults(),
						script: scriptBuilder?.scriptText
					}
				}

				const creative = this.creative = (await this.$Data.createUpdateCreative(data))?.Data;

				this.isLoading++;
				await this.info.saveBriefAnswers(creative?.Oid).finally(() => this.isLoading--);
				
				const insertions = (creativeOpts?.insertions || []).filter(f => f.__src);
				if (insertions?.length) {
					const insertionResults = await Promise.all(insertions.map(f => this.$Data.createUpdatePureInsertion({
						..._.omit(f, ['__src', '__inMemory']),
						Oid: null,
						CreativeId: creative?.Oid
					})));
					
					if (__DEBUG__)
						console.debug('newBriefCreative insertions', { insertions, insertionResults });
					await Promise.all(insertions.map((f, idx) => this.$azure.uploadInsertionAudio(insertionResults[idx]?.Data, f.__src)));
				}

				if (__DEBUG__)
					console.debug(`Save creative done ${this.creative?.Oid}`);
			},
			onDefaultVoicesReplacing(v) {
				if (!this.hasNotLoadedVoices && !!v) {
					this.hasNotLoadedVoices = true;
					this.initiateSteps(7, true, true);
					this.defaultVoicesProcessed = false;
				}
			},
			async onDefaultVoicesReplaced() {
				if (!this.hasNotLoadedVoices) return;
				if (__DEBUG__)
					console.debug('onDefaultVoicesReplaced');

				if (!this.creativeOpts) return;

				const insertions = this.creativeOpts.insertions.map(f => {
					f.__src = this.tracker?.tracksBase64[f.Oid] || f.__src;
					return f;
				});
				const newOpts = await helper.generateCreative({ ...this.creativeOpts.opts, state: null, insertions });
				newOpts.insertions.map(f => {
					const track = this.tracker.tracks.find(t => t.id === f.Oid);
					if (track && f.__src) {
						this.tracker.setBase64ForTrack(track, f.__src, false);
					}
				});
				
				//Reload all data to ensure it is ok
				this.tracker.tracks.map(f => f.__isSrcLoaded = false);
				await this.tracker.loadData();

				this.defaultVoicesProcessed = true;
				this.initiateSteps(6, true, true);
				if (__DEBUG__)
					console.debug('onDefaultVoicesReplaced new opts', { newOpts });
			},
			onActivate() {
				if (!this.$gettingCreativeOpts) {
					this.generate();
					this.activatingVoicesStep.time = this.info?.scriptBuilder()?.voicesActivatedTime ? (this.info.scriptBuilder().leftForVoicesActivation * 1000 || 0) : 60000;
				}
			},
			reloadScript() {
				this.isFinished = false;
				this.generate(true);
			},
			changeScript() {
				this.$emit('page', 'scriptBuilder');
			},
			download() {
				this.isDownloading = true;
				this.tracker?.downloadFinalAudio().finally(() => this.isDownloading = false);
			},
			async togglePlay() {
				if (!this.isPlaying) {
					if (this.hasNotLoadedVoices && !this.defaultVoicesProcessed) {
						this.$notifyWarning(this.$tCaption('We are preparing your voices, the preview will use default voices.'));
					}
					await this.tracker?.play(0);
					this.handlePlay();
				} else {
					this.tracker?.stop();
				}
			},
			handlePlay() {
				setImmediate(() => {
					const tracker = this.$refs.creativeDetail.$refs.tracker;
					const ctx = tracker.audioContext;
					this.$analyser = ctx.createAnalyser();
					tracker.masterTrackEffects.output.connect(this.$analyser);
					this.$analyser.minDecibels = -140;
					this.$analyser.maxDecibels = 0;
					
					this.$freqs = new Uint8Array(this.$analyser.frequencyBinCount);
					this.$times = new Float32Array(this.$analyser.frequencyBinCount);

					requestAnimationFrame(this.draw);
				});
			},
			draw() {
				if (!this.$analyser || !this.$refs.canvas) return;
				var SMOOTHING = 0.8;
				var FFT_SIZE = 2048;
				this.$analyser.smoothingTimeConstant = SMOOTHING;
				this.$analyser.fftSize = FFT_SIZE;

				// Get the frequency data from the currently playing music
				
				this.$analyser.getByteFrequencyData(this.$freqs);

				
				this.$analyser.getFloatTimeDomainData(this.$times);

				const canvas = this.$refs.canvas;
				const canvasWidth = this.canvasWidth = this.$refs.lineHolder?.offsetWidth - 8;

				var drawContext = canvas.getContext('2d');
				drawContext.clearRect(0, 0, canvasWidth, canvas.height);

				const step = 5;

				if (!this.$displayData) {
					this.$displayData = [];
				}

				// Draw the time domain chart.
				let counter = 0;

				if (this.$refs.creativeDetail?.$refs.tracker?.isPlaying) {
					for (let k = 0; k < this.$analyser.frequencyBinCount; k += step) {
						let value = this.$times[k];
						if (value < 0.05) {
							value *= 16;
						} else if (value < 0.1) {
							value *= 8;
						} else if (value < 0.15) {
							value *= 4;
						}
						if (value < 0.2) {
							value *= 3;
						}

						value *= 0.02;

						value += (this.$freqs[k] / 256);
						let percent = Math.min(1, Math.max(0, Math.abs(value)));
						let height = canvas.height * percent;
						this.$displayData[counter] = Math.max(this.$displayData[counter] || 0, height);

						counter++;
					}
				}

				let barWidth = canvasWidth / (this.$analyser.frequencyBinCount / step);

				this.$displayData.map((height, k) => {
					drawContext.fillStyle = this.info.brandingVars['success-bg'] || '#fff';
					drawContext.fillRect(k * barWidth, canvas.height, step * 0.25, -height);

					this.$displayData[k] = Math.max(0, this.$displayData[k] - Math.max(0.5, this.$displayData[k]/200));
				});

				if (this.$refs.creativeDetail?.$refs.tracker?.isPlaying || _.max(this.$displayData) > 0) {
					requestAnimationFrame(this.draw);
				}
			},
			stop() {
				if (this.isPlaying) {
					this.tracker?.stop();
				}
			},
			next() {
				this.stop();
				this.$emit('next');
			},
			back() {
				this.stop();
				this.$emit('back');
			},
			initiateSteps(startStep, preventCheck, preventFinishedChange) {
				if (__DEBUG__)
					console.debug('initiateSteps', { startStep, preventCheck });
				if (startStep !== void 0 && !preventFinishedChange) {
					this.isFinished = false;
				}
				if (startStep === void 0 && this.currentStep === this.waitingSteps[this.waitingSteps.length - 1]) return;
				if (!(this.currentStep || startStep !== void 0)) return;

				if (startStep !== void 0) {
					this.currentStepIdx = startStep;
					this.currentStep.waitedTime = 0;
					if (this.currentStep.getTime) {
						this.currentStep.time = this.currentStep.getTime();
					}
					this.checkWaitedTime(!preventCheck);
				}
			},
			checkWaitedTime(release) {
				if (release) {
					this.waitedTime = 0;
				}
				if (this.$checkWaitedTimer) {
					clearTimeout(this.$checkWaitedTimer);
				}
				const waitedTime = (this.currentStep.isPersonalTime ? this.currentStep.waitedTime : this.waitedTime) || 0;
				const totalTime = this.currentStep.isPersonalTime ? this.currentStep.time : this.totalTime;
				if (this.currentStep && waitedTime < totalTime) {
					const time = parseInt(Math.random() * 400);
					this.$checkWaitedTimer = setTimeout(() => {
						this.waitedTime += time;
						this.currentStep.waitedTime += time;
						this.currentPercentage = Math.min(90, waitedTime / totalTime * 100);
						this.checkWaitedTime();
					}, time);
				}
			},
			async getCreativeOpts() {
				const voiceSelector = this.info.voiceSelector();
				const isArray = Array.isArray(this.script);
				const musicSelection = this.info.musicSelection();
				this.filesDownloadingState = {};
				this.hasNotLoadedVoices = false;

				if (__DEBUG__)
					console.debug('ECStep AudioGenerator');
				
				let opts = null;
				if (isArray) {
					const musicElem = this.script.find(f => f.type.toLowerCase() === 'music' && !f.disabled);
					const musicEndElem = musicElem && this.script.find(f => f.type.toLowerCase() === 'musicend');
					const durationElem = this.script.find(f => f.type.toLowerCase() === 'duration');
					const music = musicElem && {
						adjectives: this.info.adjectives(),
						length: musicElem?.length,
						startAfterText: musicElem?.afterText,
						endAfterText: musicEndElem?.afterText,
						selectedMedia: musicSelection?.selectedMedia,
						media: musicSelection?.media?.filter(f => f?.Id),
					};

					const phrases = this.script.filter(f => ['text', 'sfx'].includes(f.type.toLowerCase())).filter(f => !f.disabled).map(f => ({
						text: f.value,
						voice: f.actualVoice,
						trackNames: f.trackNames,
						trackTemplates: f.trackTemplates,
						type: f.type,
						phonemes: f.phonemes,
						buildInSingleCall: f.buildInSingleCall
					}));

					const ambienceSFX = this.script.map((f, idx) => ({ f, idx })).filter(({ f }) => ['ambience'].includes(f.type.toLowerCase())).filter(({ f }) => !f.disabled).map(({ f, idx }) => ({
						text: f.value,
						voice: f.actualVoice,
						startAfterText: f.afterText,
						endAfterText: this.script.find((f, i) => ['ambience-end'].includes(f.type.toLowerCase()) && i > idx)?.afterText,
						trackNames: f.trackNames,
						trackTemplates: f.trackTemplates,
						type: f.type
					}));
					
					let creativeTemplate = this.info.brief.CreativeBriefProcessingTemplate;

					const templateElem = this.script.find(f => f.type.toLowerCase() === 'processing-template');
					if (templateElem?.keyword) {
						const results = (await this.$Data.getExternalApi('ProcessingTemplate/List', `filterCriteria=[["Keyword", "=", "${templateElem.keyword}"]]`))?.Results;
						if (results.length && results[0]?.CreativeTemplate) {
							creativeTemplate = (await this.$Data.getExternalApi('ProcessingTemplate/Get', `Id=${results[0].Oid}`))?.CreativeTemplate;
						}
					}

					this.$eventsBus.$emit('easy-creative--add-debug-info', 'processingTemplate', {
						Name: creativeTemplate?.Name,
						TracksConfig: creativeTemplate?.JsonConfig?.aggregatedTracks,
						Template: creativeTemplate
					});

					opts = {
						phrases,
						music,
						duration: durationElem,
						creativeTemplate,
						aimLength: durationElem?.isFixed ? durationElem?.length : null,
						state: (state) => !this.creativeOpts && this.initiateSteps(state + 3, true),
						onProgress: (objectId, percent) => this.setDownloadState(objectId, percent),
						ambienceSFX
					}
				} else {
					opts = {
						phrases: [{
							text: this.script,
							voice: voiceSelector?.getRandomVoice()
						}],
						music: {
							adjectives: this.info.adjectives()
						},
						creativeTemplate: this.info.brief.CreativeBriefProcessingTemplate,
						state: (state) => !this.creativeOpts && this.initiateSteps(state + 3, true)
					}
				}
				if (this.$gettingCreativeOpts) return this.$gettingCreativeOpts;
				if (__DEBUG__)
					console.debug('getCreativeOpts', { opts });
				this.$gettingCreativeOpts = helper.generateCreative(opts).finally(() => delete this.$gettingCreativeOpts);
				return this.$gettingCreativeOpts;
			},
			setDownloadState(id, percent) {
				if (__DEBUG__)
					console.debug(`Download onDownloadProgress ${id} ${percent}`);
				this.$set(this.filesDownloadingState, id, percent);
			},
			onCreativeLoaded(v) {
				if (__DEBUG__)
					console.debug('Creative loaded', v);
				if (v)
					this.isFinished = true;
				this.creativeTotalTime = parseInt(this.$refs.creativeDetail?.$refs?.tracker?.totalDuration * 10) / 10 || 0;
			},
			async generate(reload) {
				this.stop();
				this.initiateSteps(0);
				if (reload)
					await this.info.scriptBuilder().loadAiScript();

				this.creativeOpts = null;
				this.$nextTick(async () => {
					this.creativeOpts = await this.getCreativeOpts();
				});
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--audio-generator {
		min-height: 300px;

		@media all and (min-width: 720px) {
			width: 720px;
		}

		.play-button {
			margin: 4px;
			padding: 10px 20px;
			border-radius: 4px;
			background: #fff;
			box-shadow: 2px 2px 13px 2px rgba(0,0,0,0.1);
			transition: 0.3s;
			cursor: pointer;

			&:hover {
				box-shadow: 2px 2px 13px 2px $base-accent-transparent;
				color: $base-accent;
			}
		}

		.line-holder {
			position: relative;
			margin-top: 20px;

			.waveform-canvas {
				position: absolute;
				left: 4px;
				right: 4px;
				top: -30px;
				pointer-events: none;
				user-select: none;
				color: #fff;
			}
		}

		.image-item {
			width: 100%;
			img {
				max-height: 300px;
				width: fit-content;
			}
		}
	}
</style>
